import {Box, ButtonBase, IconButton, InputBase, MenuItem, Select, Stack, Switch, Tooltip, Typography} from "@mui/material";
import React, {useContext, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, BoltIcon, Delete02Icon, InformationCircleIcon, LanguageCircleIcon, Tag01Icon} from "@hugeicons/core-free-icons";
import {CreateOrganizationModal} from "../../organizations/RegistryOrganizationsPage";
import {SignInContext} from "../../../context/SignInContext";
import {useCatalog} from "../../../context/CatalogContext";
import {createLanguageConfig, LanguageConfigurationsNew} from "./LanguageConfigurationsNew";
import {AUDIO_ENCODINGS, GEMINI_VOICE_NAMES} from "./voiceOptions";
import {filterByOrganization} from "../../../utils/organizationAccess";
import {controlSx, FieldHelper, FieldLabel, selectSx} from "../../../components/modals/formFieldShared";

// https://docs.cloud.google.com/text-to-speech/docs/gemini-tts
const TTS_MODELS = [
  {model: "gemini-3.1-flash-tts-preview", name: "Gemini"},
  {model: null, name: "Default Model (Free)"},
];

const DEFAULT_VOICE_GEMINI = {
  voice: {modelName: TTS_MODELS[0].model, name: "", languageCode: ""},
  audioConfig: {audioEncoding: "LINEAR16", pitch: 0, speakingRate: 1},
  input: {prompt: ""},
};

export const SectionHeaderNew = ({icon, title, description}) => (
  <Stack direction={"row"} gap={1.5} alignItems={"flex-start"} sx={{mb: 2}}>
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: "10px",
      flexShrink: 0, bgcolor: "rgba(132, 145, 247, 0.12)", color: "#8491F7",
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>{title}</Typography>
      {description && <Typography sx={{fontSize: 12.5, color: "#8A8AA0", mt: 0.25}}>{description}</Typography>}
    </Box>
  </Stack>
);

// Segmented pill switch - same pattern as VersionTypeSwitch in registry-project/SubProjectSelect.js
const SegmentedSwitch = ({options, value, onChange}) => (
  <Stack role={"radiogroup"} direction={"row"} sx={{
    p: 0.375, gap: 0.375, borderRadius: "999px", bgcolor: "#FAFAFD",
  }}>
    {options.map(option => {
      const selected = option.value === value;
      return (
        <ButtonBase
          key={option.value}
          role={"radio"}
          aria-checked={selected}
          onClick={() => !selected && onChange(option.value)}
          sx={{
            height: 36, px: 2, borderRadius: "999px", fontSize: 13, fontWeight: 600,
            color: selected ? "#4B55C9" : "#6B6B80", bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "transparent",
            transition: "background-color .15s ease, color .15s ease",
            "&:hover": {color: selected ? "#4B55C9" : "#23264D", bgcolor: selected ? "rgba(132, 145, 247, 0.18)" : "#ECECF3"},
          }}
        >
          {option.label}
        </ButtonBase>
      );
    })}
  </Stack>
);

const FlatSelect = ({label, value, onChange, helper, children, displayEmpty, renderValue, multiple}) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <Select
      value={value} onChange={onChange} sx={selectSx} displayEmpty={displayEmpty} renderValue={renderValue} multiple={multiple}
      MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
    >
      {children}
    </Select>
    {helper && <FieldHelper>{helper}</FieldHelper>}
  </Box>
);

const FlatField = ({label, value, onChange, helper, placeholder, type, multiline, rows, inputProps}) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <Box sx={{...controlSx, ...(multiline && {height: "auto", alignItems: "flex-start", py: 1})}}>
      <InputBase
        type={type} placeholder={placeholder} value={value ?? ""} onChange={onChange} multiline={multiline} minRows={rows}
        inputProps={{"aria-label": label, ...inputProps}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
      />
    </Box>
    {helper && <FieldHelper>{helper}</FieldHelper>}
  </Box>
);

// One sub project of the create form; key identifies the row (and its configuration) before it has an id
export const newSubProjectRow = () => ({key: uuidv4(), name: "", description: "", languageId: null});

// Name a sub project gets: the typed name, else the language's name (as the backend defaults it)
export const subProjectRowName = (row, languages) =>
  (row.name || "").trim() || languages.find(item => item.id === row.languageId)?.language || "";

// Sub projects of a new project: name, description and a required language each. Several can share a language.
const SubProjectRows = ({rows, languages, onChange}) => {
  const update = (key, field, value) => onChange(rows.map(row => row.key === key ? {...row, [field]: value} : row));

  return (
    <Stack gap={1.25}>
      {rows.map((row, index) => (
        <Box key={row.key} sx={{p: 1.5, border: "1px solid #ECECF3", borderRadius: "14px", bgcolor: "#fff"}}>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{mb: 1}}>
            <Typography sx={{fontSize: 12, fontWeight: 700, color: "#6B6B80", textTransform: "uppercase", letterSpacing: "0.05em"}}>
              Sub project {index + 1}
            </Typography>
            <Tooltip title={rows.length > 1 ? "Remove sub project" : "A project needs at least one sub project"}>
              <span>
                <IconButton
                  size={"small"} disabled={rows.length <= 1} aria-label={"Remove sub project"}
                  onClick={() => onChange(rows.filter(item => item.key !== row.key))}
                  sx={{color: "#8A8AA0", "&:hover": {color: "#D14343", bgcolor: "rgba(209, 67, 67, 0.08)"}}}
                >
                  <HugeiconsIcon icon={Delete02Icon} size={16}/>
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Stack gap={1.5}>
            <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
              <Box sx={{flex: 1}}>
                <FlatSelect
                  label={"Language *"} displayEmpty value={row.languageId || ""}
                  renderValue={id => languages.find(item => item.id === id)?.language || "Select a language"}
                  onChange={e => update(row.key, "languageId", e.target.value || null)}
                >
                  {languages.map(item => <MenuItem key={item?.id} value={item?.id}>{item?.language} ({item?.code})</MenuItem>)}
                </FlatSelect>
              </Box>
              <Box sx={{flex: 1}}>
                <FlatField
                  label={"Name"} value={row.name} onChange={e => update(row.key, "name", e.target.value)}
                  placeholder={languages.find(item => item.id === row.languageId)?.language || "Defaults to the language name"}
                  inputProps={{maxLength: 255}}
                />
              </Box>
            </Stack>
            <FlatField
              label={"Description"} value={row.description} onChange={e => update(row.key, "description", e.target.value)}
              multiline rows={2} inputProps={{maxLength: 4000}}
            />
          </Stack>
        </Box>
      ))}
      <ButtonBase
        onClick={() => onChange([...rows, newSubProjectRow()])}
        sx={{
          alignSelf: "flex-start", gap: 0.75, height: 36, px: 1.75, borderRadius: "999px", fontSize: 13, fontWeight: 600,
          color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
        }}
      >
        <HugeiconsIcon icon={Add01Icon} size={16}/>
        Add sub project
      </ButtonBase>
    </Stack>
  );
};

// multipleLanguages: edit the project's sub projects (creation), each becomes its own flow
export const ProjectFormNew = ({project, setProject, languages, organizations, fetchData, multipleLanguages}) => {
  const [createOrganization, setCreateOrganization] = useState(false);
  const {items: myVoiceModels} = useCatalog("myVoiceModels", {refreshOnMount: !!multipleLanguages});
  const {items: myTranscribeModels} = useCatalog("myTranscribeModels", {refreshOnMount: !!multipleLanguages});
  const {items: myBackendConfigurations} = useCatalog("myBackendConfigurations", {refreshOnMount: !!multipleLanguages});
  // only public items and those of the project's organization can be used
  const voiceModels = filterByOrganization(myVoiceModels, project?.organization);
  const transcribeModels = filterByOrganization(myTranscribeModels, project?.organization);
  const backendConfigurations = filterByOrganization(myBackendConfigurations, project?.organization);
  const currentUser = useContext(SignInContext);
  const isAdmin = currentUser?.currentUser?.roles?.some(role => role?.toLowerCase() === "admin");

  const handleChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;
    updateProject(key, value);
  };

  const updateProject = (key, value) => {
    if (key) setProject(prev => ({...prev, [key]: value}));
  };

  const handleOrganizationChange = (e) => {
    const organization = organizations.find(i => i?.id === e.target.value)?.id || null;
    setProject(prev => {
      const next = {...prev, organization};
      if (!multipleLanguages) return next;
      // drop selections that are private to another organization
      const keep = (items, id) => filterByOrganization(items, organization).some(item => item.id === id) ? id : null;
      next.backendConfigurationId = keep(myBackendConfigurations, prev?.backendConfigurationId);
      next.languageConfigs = Object.fromEntries(Object.entries(prev?.languageConfigs || {}).map(([key, config]) => [
        key,
        {
          ...config,
          voiceConfiguration: {
            ...config.voiceConfiguration,
            voiceModelId: keep(myVoiceModels, config.voiceConfiguration.voiceModelId),
          },
          transcribeConfiguration: {
            ...config.transcribeConfiguration,
            transcribeModelId: keep(myTranscribeModels, config.transcribeConfiguration.transcribeModelId),
          },
        },
      ]));
      return next;
    });
  };

  // Sub project rows; their configurations are keyed by row key. The first row's language stays the project's
  // main language. A row whose language changes gets a fresh configuration (codes and models are per language).
  const subProjectRows = project?.subProjects || [];
  const updateSubProjects = (rows) => setProject(prev => {
    const main = languages.find(item => item.id === rows[0]?.languageId);
    return {
      ...prev,
      subProjects: rows,
      language: main?.id || null,
      lang: main?.lang || null,
      languageConfigs: Object.fromEntries(rows.filter(row => row.languageId).map(row => {
        const current = prev?.languageConfigs?.[row.key];
        return [row.key, current?.languageId === row.languageId ? current : {
          ...createLanguageConfig(languages.find(item => item.id === row.languageId), transcribeModels),
          languageId: row.languageId,
        }];
      })),
    };
  });

  const selectedTtsModel = project?.voiceGemini?.voice?.modelName || null;

  const handleTtsModelChange = (e) => {
    const model = e.target.value || null;
    if (!model) {
      updateProject("voiceGemini", null);
      return;
    }
    setProject(prev => ({
      ...prev,
      voiceGemini: {
        ...DEFAULT_VOICE_GEMINI,
        ...(prev?.voiceGemini || {}),
        voice: {...DEFAULT_VOICE_GEMINI.voice, ...(prev?.voiceGemini?.voice || {}), modelName: model},
      },
    }));
  };

  const updateVoiceGemini = (section, key, value) => {
    setProject(prev => ({
      ...prev,
      voiceGemini: {
        ...DEFAULT_VOICE_GEMINI,
        ...(prev?.voiceGemini || {}),
        [section]: {
          ...DEFAULT_VOICE_GEMINI[section],
          ...(prev?.voiceGemini?.[section] || {}),
          [key]: value,
        },
      },
    }));
  };

  return (
    <>
      {isAdmin && createOrganization && (
        <CreateOrganizationModal
          open={createOrganization}
          handleClose={() => setCreateOrganization(false)}
          organizations={organizations}
          onSuccess={fetchData}
        />
      )}
      <Stack direction={"column"} gap={{xs: 3, md: 4}} sx={{px: {xs: 2, md: 3}, py: {xs: 2, md: 3}}}>

        {/* General */}
        <Box>
          <SectionHeaderNew
            icon={<HugeiconsIcon icon={InformationCircleIcon} size={18}/>}
            title={"General Information"}
            description={"The basics - who owns this project and what it is."}
          />
          <Stack gap={1.5}>
            <FlatField label={"Project Name"} value={project?.name} onChange={handleChange} inputProps={{name: "name"}}/>
            <FlatField label={"Description"} value={project?.description} onChange={handleChange} multiline rows={3} inputProps={{name: "description"}}/>
            <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
              <Box sx={{flex: 1}}>
                <FieldLabel>Organization</FieldLabel>
                <Stack direction={"row"} gap={1} alignItems={"flex-start"}>
                  <Select
                    value={project?.organization || ""} sx={{...selectSx, flex: 1}}
                    onChange={handleOrganizationChange}
                    MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                  >
                    <MenuItem value={""}><em>None</em></MenuItem>
                    {organizations?.map(item => <MenuItem key={item?.id} value={item?.id}>{item?.name}</MenuItem>)}
                  </Select>
                  {isAdmin && (
                    <Tooltip title={"Create Organization"}>
                      <IconButton
                        onClick={() => setCreateOrganization(true)}
                        sx={{width: 40, height: 40, borderRadius: "10px", bgcolor: "#FAFAFD", color: "#374151", "&:hover": {bgcolor: "#ECECF3"}}}
                      >
                        <HugeiconsIcon icon={Add01Icon} size={18}/>
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
              {multipleLanguages && (
                <Box sx={{flex: 1}}>
                  <FlatSelect
                    label={"Backend Configuration"} displayEmpty value={project?.backendConfigurationId || ""}
                    onChange={e => updateProject("backendConfigurationId", e.target.value || null)}
                    helper={"Public configurations and those of the selected organization"}
                  >
                    <MenuItem value={""}><em>None</em></MenuItem>
                    {backendConfigurations.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                  </FlatSelect>
                </Box>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Language & Voice Configuration */}
        <Box>
          <SectionHeaderNew
            icon={<HugeiconsIcon icon={LanguageCircleIcon} size={18}/>}
            title={"Language & Voice Configuration"}
            description={"Configure how the assistant understands and speaks to users."}
          />
          <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
            <Box sx={{flex: 1}}>
              {multipleLanguages ? (
                <SubProjectRows rows={subProjectRows} languages={languages || []} onChange={updateSubProjects}/>
              ) : (
                <FlatSelect
                  label={"Language"} value={project?.language || ""}
                  onChange={e => {
                    const lang = languages.find(i => i.id === e.target.value);
                    setProject(prev => ({...prev, "language": lang?.id || null, "lang": lang?.lang || null}));
                  }}
                >
                  <MenuItem value={""}><em>None</em></MenuItem>
                  {languages?.map(item => <MenuItem key={item?.id} value={item?.id}>{item?.language} ({item?.code})</MenuItem>)}
                </FlatSelect>
              )}
            </Box>
            {!multipleLanguages && (
              <Box sx={{flex: 1}}>
                <FlatSelect
                  label={"Voice Model"} displayEmpty value={selectedTtsModel || ""}
                  renderValue={value => TTS_MODELS.find(item => (item.model || "") === value)?.name || "Default Model (Free)"}
                  onChange={handleTtsModelChange}
                >
                  {TTS_MODELS.map(item => <MenuItem key={item.model || "default"} value={item.model || ""}>{item.name}</MenuItem>)}
                </FlatSelect>
              </Box>
            )}
          </Stack>

          {multipleLanguages && (
            <LanguageConfigurationsNew
              languageIds={subProjectRows.filter(row => row.languageId).map(row => row.key)}
              languages={subProjectRows.map(row => ({
                id: row.key,
                languageId: row.languageId,
                language: subProjectRowName(row, languages || []),
                code: languages?.find(item => item.id === row.languageId)?.code,
              }))}
              configs={project?.languageConfigs}
              voiceModels={voiceModels}
              transcribeModels={transcribeModels}
              onChange={(key, config) => setProject(prev => ({
                ...prev,
                languageConfigs: {...(prev?.languageConfigs || {}), [key]: config},
              }))}
            />
          )}

          {!multipleLanguages && selectedTtsModel && (
            <Stack gap={1.5} sx={{mt: 1.5}}>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <Box sx={{flex: 1}}>
                  <FlatSelect label={"Voice"} value={project?.voiceGemini?.voice?.name || ""} onChange={e => updateVoiceGemini("voice", "name", e.target.value)}>
                    <MenuItem value={""}><em>None</em></MenuItem>
                    {GEMINI_VOICE_NAMES.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                  </FlatSelect>
                </Box>
                <Box sx={{flex: 1}}>
                  <FlatField
                    label={"Voice Language Code"} placeholder={"e.g. en-US"} helper={"BCP-47 code"}
                    value={project?.voiceGemini?.voice?.languageCode || ""}
                    onChange={e => updateVoiceGemini("voice", "languageCode", e.target.value)}
                  />
                </Box>
              </Stack>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <Box sx={{flex: 1}}>
                  <FlatSelect label={"Audio Encoding"} value={project?.voiceGemini?.audioConfig?.audioEncoding || "LINEAR16"} onChange={e => updateVoiceGemini("audioConfig", "audioEncoding", e.target.value)}>
                    {AUDIO_ENCODINGS.map(enc => <MenuItem key={enc} value={enc}>{enc}</MenuItem>)}
                  </FlatSelect>
                </Box>
                <Box sx={{flex: 1}}>
                  <FlatField
                    label={"Pitch"} type={"number"} helper={"-20.0 to 20.0"} inputProps={{step: 0.1, min: -20, max: 20}}
                    value={project?.voiceGemini?.audioConfig?.pitch ?? 0}
                    onChange={e => updateVoiceGemini("audioConfig", "pitch", e.target.value === "" ? null : Number(e.target.value))}
                  />
                </Box>
                <Box sx={{flex: 1}}>
                  <FlatField
                    label={"Speaking Rate"} type={"number"} helper={"0.25 to 4.0"} inputProps={{step: 0.05, min: 0.25, max: 4}}
                    value={project?.voiceGemini?.audioConfig?.speakingRate ?? 1}
                    onChange={e => updateVoiceGemini("audioConfig", "speakingRate", e.target.value === "" ? null : Number(e.target.value))}
                  />
                </Box>
              </Stack>
              <FlatField
                label={"Style Prompt"} multiline rows={2}
                placeholder={"e.g. Speak in a calm, friendly tone. Use [sigh] and [whispering] tags sparingly."}
                helper={"Optional natural-language delivery guidance, supports tags like [whispering], [laughing], [extremely fast]"}
                value={project?.voiceGemini?.input?.prompt || ""}
                onChange={e => updateVoiceGemini("input", "prompt", e.target.value)}
              />
            </Stack>
          )}
        </Box>

        {/* Classification */}
        <Box>
          <SectionHeaderNew icon={<HugeiconsIcon icon={Tag01Icon} size={18}/>} title={"Classification"} description={"Helps organize this project across the workspace."}/>
          <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
            <Box sx={{flex: 1}}>
              <FlatSelect
                label={"Status"} value={project?.status || ""}
                onChange={e => setProject(prev => ({...prev, status: e.target.value || null}))}
              >
                <MenuItem value={""}><em>None</em></MenuItem>
                <MenuItem value={"BUILDING_SCENARIOS"}>Building Scenarios</MenuItem>
                <MenuItem value={"IN_PROGRESS"}>In Progress</MenuItem>
                <MenuItem value={"TESTING"}>Testing</MenuItem>
                <MenuItem value={"PRODUCTION"}>Production</MenuItem>
              </FlatSelect>
            </Box>
            <Box>
              <FieldLabel>Type</FieldLabel>
              <SegmentedSwitch
                options={[{value: "VOICE_BOT", label: "Voice Assistant"}, {value: "CHAT_BOT", label: "Chatbot Assistant"}]}
                value={project?.projectType || "VOICE_BOT"}
                onChange={value => updateProject("projectType", value)}
              />
            </Box>
          </Stack>
        </Box>

        {/* Availability */}
        <Box>
          <SectionHeaderNew icon={<HugeiconsIcon icon={BoltIcon} size={18}/>} title={"Availability"}/>
          <Stack
            direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}
            sx={{
              borderRadius: "14px", p: 2,
              bgcolor: project?.is_live ? "rgba(47, 154, 106, 0.08)" : "rgba(168, 106, 12, 0.08)",
            }}
          >
            <Stack direction={"row"} gap={1.5} alignItems={"center"}>
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                bgcolor: project?.is_live ? "#2F9A6A" : "#A86A0C",
              }}/>
              <Box>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>
                  {project?.is_live ? "Live" : "Maintenance Mode"}
                </Typography>
                <Typography sx={{fontSize: 12.5, color: "#6B6B80"}}>
                  {project?.is_live
                    ? "Users are being served by this project normally."
                    : "Calls are redirected to the configured fallback number."}
                </Typography>
              </Box>
            </Stack>
            <Switch checked={!!project?.is_live} onChange={(e, checked) => updateProject("is_live", checked)}/>
          </Stack>
        </Box>
      </Stack>
    </>
  );
};
